import { Box, Skeleton, Typography } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import type { ValuationHubCoreModelRealEstateAvmEquipmentPriceRange } from "@/api/generatedApi";
import { EquipmentHighIcon, EquipmentMediumIcon, EquipmentSimpleIcon, EquipmentVeryHighIcon } from "@/assets";
import { CustomChip } from "@/components/customChip/CustomChip";

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createStyles = (theme: Theme) => ({
  grid: {
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      sm: "repeat(4, 1fr)",
      lg: "repeat(4, 1fr)",
    },
    gap: theme.analysisLayout.sectionGap,
  },
  card: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexDirection: "column",
    border: "1px solid",
    borderColor: "divider",
    borderRadius: "4px",
    padding: theme.analysisLayout.cardPaddingCompact,
  },
  selectedCard: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.semanticColors.surface.blueBg,
  },
  selectedChip: {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
    position: "absolute" as const,
    top: 0,
    left: "50%",
    transform: "translate(-50%, -50%)",
    whiteSpace: "nowrap" as const,
  },
  icon: {
    fontSize: theme.analysisLayout.cardIconSize,
  },
  priceValue: {
    fontWeight: 600,
    fontSize: "14px",
  },
});

const equipmentStandardIconMap = {
  simple: EquipmentSimpleIcon,
  medium: EquipmentMediumIcon,
  high: EquipmentHighIcon,
  veryHigh: EquipmentVeryHighIcon,
} as const;

type EquipmentStandard = {
  key: keyof typeof equipmentStandardIconMap;
  label: string;
  equipment: string;
};

interface EquipmentStandardsGridProps {
  standards: readonly EquipmentStandard[];
  ranges?: ValuationHubCoreModelRealEstateAvmEquipmentPriceRange[] | null;
  livingArea?: number | null;
  avmValue?: number | null;
  isLoading?: boolean;
}

const formatPrice = (value?: number | null): string =>
  value != null ? `${Math.round(value).toLocaleString("de-DE")} €` : "—";

const formatPricePerSqm = (value?: number | null, livingArea?: number | null): string => {
  if (value == null || !livingArea) {
    return "—";
  }

  return `${Math.round(value / livingArea).toLocaleString("de-DE")} €/m²`;
};

export const EquipmentStandardsGrid = ({
  standards,
  ranges,
  livingArea,
  avmValue,
  isLoading = false,
}: EquipmentStandardsGridProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation("PricePositioningPanel");
  const styles = createStyles(theme);
  const cardValues = standards.map(
    (standard) => ranges?.find((entry) => entry.equipment === standard.equipment)?.value,
  );
  const selectedCardIndex =
    typeof avmValue === "number"
      ? cardValues.reduce(
          (selectedIndex, cardValue, index) => (cardValue != null && cardValue <= avmValue ? index : selectedIndex),
          -1,
        )
      : -1;

  return (
    <Box sx={styles.grid}>
      {standards.map((standard, index) => {
        const Icon = equipmentStandardIconMap[standard.key];
        const range = ranges?.find((entry) => entry.equipment === standard.equipment);
        const price = range?.value;
        const isSelected = index === selectedCardIndex;

        return (
          <Box
            sx={{ ...styles.card, ...(isSelected ? styles.selectedCard : {}) }}
            key={standard.key}
            data-cy={`price-positioning-equipment-standard-${standard.key}`}
          >
            {isSelected && (
              <CustomChip
                label={t("yourProperty")}
                chipType="primary"
                sx={styles.selectedChip}
                data-cy="price-positioning-selected-equipment-standard"
              />
            )}
            <Icon color="primary" sx={styles.icon} />
            <Typography>{standard.label}</Typography>
            <Typography sx={styles.priceValue} data-cy={`price-positioning-equipment-standard-${standard.key}-range`}>
              {isLoading ? <Skeleton animation="wave" variant="rounded" width={130} height={20} /> : formatPrice(price)}
            </Typography>
            <Typography variant="hintText">
              {isLoading ? (
                <Skeleton animation="wave" variant="rounded" width={90} height={16} />
              ) : (
                formatPricePerSqm(price, livingArea)
              )}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};
